import { useMemo, useState } from "react";
import type { SyntheticEvent } from "react";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { martialArts } from "../../../content";
import type {
	MartialArtsAchievement,
	MartialArtsCamp,
	MartialArtsCertificate,
	MartialArtsDiscipline,
	MartialArtsGrade,
	MartialArtsResult,
	MartialArtsRole
} from "../../../types/content";
import { imageFor } from "../../../utils/martialArtsImages";

/**
 * ============================================================================
 * Martial Arts & Service — the /martial-arts route.
 * ============================================================================
 *
 * Direct consumer of the content layer (no bridge, V.4), on the Stories and
 * Platforms precedent. To change what this page says, edit
 * `src/content/data/martialArts.json` — not this file.
 *
 * NOT ONE NUMBER ON THIS PAGE IS TYPED. Podium totals, first places and the
 * years-active span are all computed from `achievements`, which is the direct
 * application of D-065: a count that is stated in two places is a count that
 * will disagree with itself. `awards.json` carries the same Federation Cup
 * result in prose, and the two agree because the championship's canonical name
 * is byte-identical in both files and asserted as such by the verification
 * sweep — not because someone remembered to update both.
 *
 * THE IMAGE SLOT IS PRESENT AND EMPTY. Every achievement, grade, camp and
 * certificate may carry a redacted scan; none does today, and the page is
 * complete without any. See `src/utils/martialArtsImages.ts` for the procedure
 * and for the redaction constraint that governs it.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044), so the search result cannot drift from the page's opening line.
 *
 * IT DESCRIBES THE PAGE, AND MAKES NO CLAIM THE LAYER CANNOT SETTLE. An earlier
 * draft opened "Two decades of competitive martial arts ... kept alongside the
 * engineering career" and both halves were wrong: the recorded results span
 * 2015-2018, and `experience.json` starts 2018-09, so the record ran BEFORE the
 * career rather than beside it. Neither figure was in the data -- they were
 * written, which is how a standfirst becomes the one line on the page nothing
 * can check. Spans and counts belong to the stat cards, which compute them.
 */
export const MARTIAL_ARTS_STANDFIRST =
	"Competitive karate and Muay Thai, and service in the National Cadet Corps — the grades held, the placings won, the camps attended and the officiating roles served.";

/* ========================= COMPUTED, NEVER TYPED ========================= */

const podiumOf = (results: MartialArtsResult[]): MartialArtsResult[] =>
	results.filter((r: MartialArtsResult) => r.position !== null && r.position <= 3);

const allResults = (disciplines: MartialArtsDiscipline[]): MartialArtsResult[] =>
	disciplines.flatMap((d: MartialArtsDiscipline) => d.achievements.flatMap((a: MartialArtsAchievement) => a.results));

/**
 * Leads are the results a reader should see without opening anything:
 * International and National. Everything below that level goes into the
 * collapsible list, newest-first. The rule is a level test rather than a
 * hand-picked id list so that adding an event places itself.
 */
const LEAD_LEVELS = ["International", "National"];
const isLead = (a: MartialArtsAchievement): boolean => a.level !== undefined && LEAD_LEVELS.includes(a.level);

/** Newest first; ties keep the order the content file states. */
const byYearDesc = (a: MartialArtsAchievement, b: MartialArtsAchievement): number => Number(b.year) - Number(a.year);

/* ============================== TINTS ============================== */

/**
 * Position and level are INFORMATION here, not decoration — a first place and
 * a third are different facts, and so are an international and a district
 * event. D-061 ruled that a tint carrying information keeps its hue; only the
 * tints that carry none were converged onto the indigo triple. Whole class
 * strings, because Tailwind's JIT scans source text and never sees a
 * constructed name.
 */
const POSITION_TINT: Record<number, string> = {
	1: "border-[var(--cat-amber-border)] text-[var(--cat-amber-text)] bg-[var(--cat-amber-fill)]",
	2: "border-[var(--cat-slate-border)] text-[var(--cat-slate-text)] bg-[var(--cat-slate-fill)]",
	3: "border-[var(--cat-stone-border)] text-[var(--cat-stone-text)] bg-[var(--cat-stone-fill)]"
};

const LEVEL_TINT: Record<string, string> = {
	International: "border-[var(--cat-violet-border)] text-[var(--cat-violet-text)] bg-[var(--cat-violet-fill)]",
	National: "border-[var(--cat-indigo-border)] text-[var(--cat-indigo-text)] bg-[var(--cat-indigo-fill)]",
	State: "border-[var(--cat-sky-border)] text-[var(--cat-sky-text)] bg-[var(--cat-sky-fill)]",
	District: "border-[var(--cat-teal-border)] text-[var(--cat-teal-text)] bg-[var(--cat-teal-fill)]",
	"Sub-division": "border-[var(--cat-stone-border)] text-[var(--cat-stone-text)] bg-[var(--cat-stone-fill)]"
};

const QUIET_TINT = "border-[var(--cat-indigo-border)] text-[var(--cat-indigo-text)] bg-[var(--cat-indigo-fill)]";

const ordinal = (position: number): string => {
	if (position === 1) return "1st";
	if (position === 2) return "2nd";
	if (position === 3) return "3rd";
	return `${position}th`;
};

/* WHY --text-muted AND NOT --text-faint ON THE SECTION GROUND.
   `--text-faint` was tuned in D-037 to 4.68:1 against `--surface-card` -- it is
   CARD-SCOPED by construction, and its own token comment says so. On this
   page's ground (rgb(28,28,39) in dark) it measures 4.44:1 and FAILS the 4.5
   floor. The first sweep of this route caught it as 36 elements x 3 widths =
   108 failures, all of them the section labels, the unit terms and the year
   columns, which sit on the ground rather than on a card. Text inside a
   `card-face` keeps `--text-faint` (5.03:1) and is untouched. */

/* ============================== PIECES ============================== */

/**
 * A redacted scan, when one exists. Renders nothing at all today.
 *
 * `alt` is required by `MartialArtsImage`, so there is no path through this
 * component that produces an undescribed image.
 */
const RecordImage = ({ id }: { id: string }): JSX.Element | null => {
	const image = imageFor(id);
	const [open, setOpen] = useState(false);
	if (!image) return null;

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="mt-3 inline-flex min-h-[24px] items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
				aria-label={`View ${image.imageType} for ${image.alt}`}
			>
				<img src={image.src} alt="" aria-hidden="true" className="h-6 w-6 rounded object-cover" />
				{image.imageType === "certificate" ? "View certificate" : "View photo"}
			</button>

			{open && (
				<div
					role="dialog"
					aria-modal="true"
					aria-label={image.alt}
					onClick={() => setOpen(false)}
					className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-4"
				>
					<img src={image.src} alt={image.alt} className="max-h-[90vh] max-w-full rounded-xl" />
				</div>
			)}
		</>
	);
};

const ResultPills = ({ results }: { results: MartialArtsResult[] }): JSX.Element | null => {
	if (!results.length) return null;
	return (
		<div className="mt-3 flex flex-wrap gap-2">
			{results.map((r: MartialArtsResult) => (
				<span
					key={`${r.discipline}-${String(r.position)}`}
					className={`pill-tint pill-tint-wrap ${
						r.position === null ? QUIET_TINT : POSITION_TINT[r.position] ?? QUIET_TINT
					}`}
				>
					{r.position === null ? `${r.discipline} — participated` : `${r.discipline} ${ordinal(r.position)}`}
				</span>
			))}
		</div>
	);
};

const AchievementCard = ({ achievement }: { achievement: MartialArtsAchievement }): JSX.Element => (
	<div className="card-shell">
		<div className="card-face flex h-full flex-col p-5">
			{achievement.level && (
				<span
					className={`pill-tint pill-tint-upper pill-tint-wrap self-start ${
						LEVEL_TINT[achievement.level] ?? QUIET_TINT
					}`}
				>
					{achievement.level}
				</span>
			)}

			<h4 className="mt-3 text-base font-bold leading-snug text-[var(--text-strong)]">{achievement.event}</h4>
			<p className="mt-1 text-sm text-[var(--text-faint)]">{achievement.date ?? achievement.year}</p>

			<ResultPills results={achievement.results} />

			{achievement.note && <p className="mt-3 text-sm text-[var(--text-muted)]">{achievement.note}</p>}

			<RecordImage id={achievement.id} />
		</div>
	</div>
);

/** A compact row for the collapsible list — same facts, less furniture. */
const AchievementRow = ({ achievement }: { achievement: MartialArtsAchievement }): JSX.Element => (
	<li className="flex flex-col gap-2 border-b border-[var(--border)] py-4 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
		<span className="w-16 shrink-0 text-sm font-semibold tabular-nums text-[var(--text-muted)]">
			{achievement.year}
		</span>
		<div className="min-w-0 flex-1">
			<p className="text-sm font-semibold text-[var(--text-strong)]">{achievement.event}</p>
			<div className="mt-2 flex flex-wrap items-center gap-2">
				{achievement.level && (
					<span className={`pill-tint pill-tint-wrap ${LEVEL_TINT[achievement.level] ?? QUIET_TINT}`}>
						{achievement.level}
					</span>
				)}
				{achievement.results.map((r: MartialArtsResult) => (
					<span
						key={`${r.discipline}-${String(r.position)}`}
						className={`pill-tint pill-tint-wrap ${
							r.position === null ? QUIET_TINT : POSITION_TINT[r.position] ?? QUIET_TINT
						}`}
					>
						{r.position === null
							? `${r.discipline} — participated`
							: `${r.discipline} ${ordinal(r.position)}`}
					</span>
				))}
			</div>
			{achievement.note && <p className="mt-2 text-sm text-[var(--text-muted)]">{achievement.note}</p>}
			<RecordImage id={achievement.id} />
		</div>
	</li>
);

const StatCard = ({ value, label, detail }: { value: string; label: string; detail?: string }): JSX.Element => (
	<div className="card-shell">
		<div className="card-face flex h-full flex-col p-6">
			<span className="text-4xl font-bold leading-none tabular-nums text-[var(--text-strong)]">{value}</span>
			<span className="mt-3 text-sm font-semibold text-[var(--text-primary)]">{label}</span>
			{detail && <span className="mt-2 text-sm text-[var(--text-muted)]">{detail}</span>}
		</div>
	</div>
);

/* ============================ DISCIPLINE ============================ */

const DisciplineBlock = ({ discipline }: { discipline: MartialArtsDiscipline }): JSX.Element => {
	const [open, setOpen] = useState(false);

	const leads = useMemo(
		() => discipline.achievements.filter(isLead).slice().sort(byYearDesc),
		[discipline.achievements]
	);
	const rest = useMemo(
		() =>
			discipline.achievements
				.filter((a: MartialArtsAchievement) => !isLead(a))
				.slice()
				.sort(byYearDesc),
		[discipline.achievements]
	);

	return (
		/* THE `id` IS THE LANDING SITE FOR THE HOME TEASER (D-100). The teaser's
		   three cards each name a discipline, so the discipline SECTION is what
		   they link to — an achievement-level anchor would drop a reader into the
		   middle of a table with no heading above them.

		   NOT `${discipline.id}-heading`, which already exists three lines down
		   and is spoken for: it is the `aria-labelledby` target, and pointing a
		   hash at it would scroll to the heading rather than to the section, so
		   `scroll-mt-24` would clear the navbar of the wrong element. Two ids on
		   one block is cheaper than one id doing two jobs. */
		<section
			id={discipline.id}
			className="mt-16 scroll-mt-24 first:mt-0"
			aria-labelledby={`${discipline.id}-heading`}
		>
			<div className="flex flex-wrap items-center gap-3">
				<h2 id={`${discipline.id}-heading`} className="text-2xl font-bold text-[var(--text-strong)]">
					{discipline.name}
				</h2>
				{discipline.practisingSince && (
					<span className={`pill-tint pill-tint-wrap ${QUIET_TINT}`}>Since {discipline.practisingSince}</span>
				)}
				{discipline.rank && <span className={`pill-tint pill-tint-wrap ${QUIET_TINT}`}>{discipline.rank}</span>}
				{discipline.durationYears && (
					<span className={`pill-tint pill-tint-wrap ${QUIET_TINT}`}>{discipline.durationYears} years</span>
				)}
			</div>

			{/* ---------------- NCC unit ---------------- */}
			{discipline.unit && (
				<dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
					{[
						["Wing", discipline.unit.wing],
						["Directorate", discipline.unit.directorate],
						["Group", discipline.unit.group],
						["Battalion", discipline.unit.battalion],
						["Division", discipline.unit.division]
					].map(([term, value]: string[]) => (
						<div key={term} className="border-l-2 border-[var(--border)] pl-4">
							<dt className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
								{term}
							</dt>
							<dd className="mt-1 text-sm font-medium text-[var(--text-strong)]">{value}</dd>
						</div>
					))}
				</dl>
			)}

			{/* ---------------- Grades ---------------- */}
			{discipline.grades && discipline.grades.length > 0 && (
				<div className="mt-6">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
						Grades held
					</h3>
					<ul className="mt-3 flex flex-wrap gap-3">
						{discipline.grades.map((g: MartialArtsGrade) => (
							<li
								key={g.id}
								className="rounded-xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--text-primary)]"
							>
								<span className="font-semibold text-[var(--text-strong)]">{g.title}</span>
								<span className="mt-1 block text-[var(--text-muted)]">
									{g.organisation}
									{g.date ? ` · ${g.date}` : ""}
								</span>
								<RecordImage id={g.id} />
							</li>
						))}
					</ul>
				</div>
			)}

			{/* ---------------- Roles ---------------- */}
			{discipline.roles && discipline.roles.length > 0 && (
				<div className="mt-6">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
						{discipline.roles.some((r: MartialArtsRole) => r.kind === "instructor")
							? "Instructing & officiating"
							: "Officiating"}
					</h3>
					<ul className="mt-3 space-y-2">
						{discipline.roles.map((r: MartialArtsRole) => (
							<li key={r.id} className="text-sm text-[var(--text-primary)]">
								<span className="font-semibold text-[var(--text-strong)]">{r.title}</span>
								<span className="text-[var(--text-muted)]">
									{" — "}
									{r.organisation}
									{r.years ? ` · ${r.years}` : ""}
									{r.note ? ` · ${r.note}` : ""}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}

			{/* ---------------- Lead achievements ---------------- */}
			{leads.length > 0 && (
				<div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
					{leads.map((a: MartialArtsAchievement) => (
						<AchievementCard key={a.id} achievement={a} />
					))}
				</div>
			)}

			{/* ---------------- The rest, collapsible ---------------- */}
			{rest.length > 0 && (
				<details
					open={open}
					onToggle={(event: SyntheticEvent<HTMLDetailsElement>) => setOpen(event.currentTarget.open)}
					className="mt-8 border-t border-[var(--border)] pt-6"
				>
					<summary className="flex min-h-[24px] cursor-pointer list-none items-center text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]">
						{open ? "Hide" : "Show"} {rest.length} further{" "}
						{discipline.kind === "service" ? "record" : "placement"}
						{rest.length === 1 ? "" : "s"}
						<span aria-hidden="true" className="ml-2">
							{open ? "↑" : "↓"}
						</span>
					</summary>
					<ul className="mt-4">
						{rest.map((a: MartialArtsAchievement) => (
							<AchievementRow key={a.id} achievement={a} />
						))}
					</ul>
				</details>
			)}

			{/* ---------------- Certificates ---------------- */}
			{discipline.certificates && discipline.certificates.length > 0 && (
				<div className="mt-8">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
						Certificates
					</h3>
					<ul className="mt-3 flex flex-wrap gap-3">
						{discipline.certificates.map((c: MartialArtsCertificate) => (
							<li
								key={c.id}
								className="rounded-xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--text-primary)]"
							>
								<span className="font-semibold text-[var(--text-strong)]">{c.name}</span>
								<span className="mt-1 block text-[var(--text-muted)]">
									Grade {c.grade} · {c.year}
								</span>
								<RecordImage id={c.id} />
							</li>
						))}
					</ul>
				</div>
			)}

			{/* ---------------- Camps ---------------- */}
			{discipline.camps && discipline.camps.length > 0 && (
				<div className="mt-8">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
						Camps attended
					</h3>
					<ul className="mt-3 space-y-2">
						{discipline.camps.map((c: MartialArtsCamp) => (
							<li key={c.id} className="text-sm text-[var(--text-primary)]">
								<span className="font-semibold tabular-nums text-[var(--text-muted)]">{c.year}</span>
								{" — "}
								<span className="text-[var(--text-strong)]">{c.name}</span>
								{c.location && <span className="text-[var(--text-muted)]">{` · ${c.location}`}</span>}
								{c.note && <span className="text-[var(--text-muted)]">{` · ${c.note}`}</span>}
								<RecordImage id={c.id} />
							</li>
						))}
					</ul>
				</div>
			)}
		</section>
	);
};

/* ============================== PAGE ============================== */

export default function MartialArts(): JSX.Element {
	const results = allResults(martialArts);
	const podium = podiumOf(results);
	const firsts = podium.filter((r: MartialArtsResult) => r.position === 1);
	const grades = martialArts.flatMap((d: MartialArtsDiscipline) => d.grades ?? []);
	const ncc = martialArts.find((d: MartialArtsDiscipline) => d.kind === "service");
	/* Which disciplines actually produced a first place — derived, because "across
	   karate and service" was typed prose about the data and would have gone quietly
	   wrong the first time a first place landed anywhere else. */
	const firstsIn = martialArts
		.filter((d: MartialArtsDiscipline) =>
			d.achievements.some((a: MartialArtsAchievement) =>
				a.results.some((r: MartialArtsResult) => r.position === 1)
			)
		)
		.map((d: MartialArtsDiscipline) => d.name)
		.join(" · ");

	return (
		<section className="section-shell" aria-labelledby="martial-arts-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Discipline &amp; Service</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="martial-arts-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Martial Arts
					</span>{" "}
					&amp; Service
				</h1>

				<p className="section-standfirst">{MARTIAL_ARTS_STANDFIRST}</p>

				{/* ================= STAT CARDS — every value computed ================= */}
				<div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
					<StatCard
						value={String(grades.length)}
						label="Grades held"
						detail={grades.map((g: MartialArtsGrade) => `${g.title} (${g.organisation})`).join(" · ")}
					/>
					<StatCard
						value={String(podium.length)}
						label="Podium finishes"
						detail="Counted from the record below, never stated separately."
					/>
					<StatCard value={String(firsts.length)} label="First places" detail={firstsIn} />
					{ncc?.rank && (
						<StatCard
							value={ncc.rank}
							label="NCC rank"
							detail={ncc.unit ? `${ncc.unit.battalion} · ${ncc.unit.division}` : undefined}
						/>
					)}
				</div>

				{/* ================= DISCIPLINES ================= */}
				<div className="mt-16">
					{martialArts.map((d: MartialArtsDiscipline) => (
						<DisciplineBlock key={d.id} discipline={d} />
					))}
				</div>

				<div className="mt-16 text-center">
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
